import dayjs from 'dayjs';
import i18n from 'i18n';
import _get from 'lodash/get';
import type { Nullable, VATReturnFrequency } from 'types/global.types';
import type {
  CashAvailableBalanceType,
  CashAvailableFactorsType,
  CashAvailableFactorType,
  CashAvailableSettingsType,
} from 'types/transactions.types';
import { VATReturnFrequencyEnum } from 'types/users.types';
import { CURRENT_MONTH, CURRENT_QUARTER, CURRENT_YEAR } from 'utils/constants';

const calculateAvailableCash = (factors: CashAvailableFactorsType): number => {
  const balances = _get(factors, 'balance.individualBalance', [])
    .filter(
      (balance: CashAvailableBalanceType) =>
        balance?.baseCurrencyBalance?.amount,
    )
    .map((balance: CashAvailableBalanceType) => ({
      ...balance,
      amount: balance?.baseCurrencyBalance?.amount,
    }));

  const provisionForIncomeTax = Object.values(
    _get(factors, 'provisionForIncomeTax', {}),
  );

  const provisionForCompanyTax = Object.values(
    _get(factors, 'provisionForCompanyTax', {}),
  );

  const provisionForVAT = Object.values(_get(factors, 'provisionForVAT', {}));

  const salesInvoiceToBePaid = _get(factors, 'salesInvoiceToBePaid', {});

  const purchasesInvoiceToBePaid = {
    active: factors.purchasesInvoiceToBePaid?.active,
    amount: (factors.purchasesInvoiceToBePaid?.amount || 0) * -1,
  };

  const socialContributions = Object.values(
    _get(factors, 'socialContributions', {}),
  );

  const provisionForSpecialVAT = Object.values(
    _get(factors, 'provisionForSpecialVAT', {}),
  );

  const normalizedFactors: CashAvailableFactorType[] = [
    ...balances,
    ...provisionForIncomeTax,
    ...provisionForCompanyTax,
    ...provisionForVAT,
    salesInvoiceToBePaid,
    purchasesInvoiceToBePaid,
    ...socialContributions,
    ...provisionForSpecialVAT,
  ];

  return normalizedFactors
    .filter(({ active }: CashAvailableFactorType) => active)
    .reduce(
      (total: number, { amount }: CashAvailableFactorType) => total + amount,
      0,
    );
};

const cashAvailableSettingsReducer = (
  factors: CashAvailableFactorsType,
): CashAvailableSettingsType => ({
  balances: _get(factors, 'balance.individualBalance', []).map(
    ({ active, IBAN }: CashAvailableBalanceType) => ({
      balance: active,
      IBAN,
    }),
  ),
  vatCurrentPeriod: _get(factors, 'provisionForVAT.currentPeriod.active'),
  vatPastPeriods: _get(factors, 'provisionForVAT.pastPeriods.active'),
  salesInvoices: _get(factors, 'salesInvoiceToBePaid.active'),
  purchaseInvoices: _get(factors, 'purchasesInvoiceToBePaid.active'),
  socialContributionsCurrentPeriod: _get(
    factors,
    'socialContributions.currentPeriod.active',
  ),
  socialContributionsPastPeriods: _get(
    factors,
    'socialContributions.pastPeriods.active',
  ),
  incomeTaxCurrentPeriod: _get(
    factors,
    'provisionForIncomeTax.currentPeriod.active',
  ),
  incomeTaxPastPeriods: _get(
    factors,
    'provisionForIncomeTax.pastPeriods.active',
  ),
  companyTaxCurrentPeriod: _get(
    factors,
    'provisionForCompanyTax.currentPeriod.active',
  ),
  companyTaxPastPeriods: _get(
    factors,
    'provisionForCompanyTax.pastPeriods.active',
  ),
  specialVatCurrentPeriod: _get(
    factors,
    'provisionForSpecialVAT.currentPeriod.active',
  ),
  specialVatPastPeriods: _get(
    factors,
    'provisionForSpecialVAT.pastPeriods.active',
  ),
});

const evaluateProvisionForVATLabels = (
  returnFrequency: VATReturnFrequency,
  currentPeriodAmount: number,
  pastPeriodAmounts: number,
): {
  currentPeriodLabel: string;
  pastPeriodsLabel: string;
} => {
  const mapper = {
    [VATReturnFrequencyEnum.quarterly]: {
      currentPeriod: {
        toRecover: i18n.t('vat_to_claim_current_quarter', {
          quarter: CURRENT_QUARTER,
        }),
        toPay: i18n.t('vat_due_current_quarter', {
          quarter: CURRENT_QUARTER,
        }),
      },
      pastPeriods: {
        toRecover: i18n.t('vat_to_claim_past_quarters'),
        toPay: i18n.t('vat_due_past_quarters'),
      },
    },
    [VATReturnFrequencyEnum.monthly]: {
      currentPeriod: {
        toRecover: i18n.t('vat_to_claim_current_month', {
          month: dayjs(new Date(CURRENT_YEAR, CURRENT_MONTH, 1)).format('MMMM'),
        }),
        toPay: i18n.t('vat_due_current_month', {
          month: dayjs(new Date(CURRENT_YEAR, CURRENT_MONTH, 1)).format('MMMM'),
        }),
      },
      pastPeriods: {
        toRecover: i18n.t('vat_to_claim_past_months'),
        toPay: i18n.t('vat_due_past_months'),
      },
    },
    [VATReturnFrequencyEnum.yearly]: {
      currentPeriod: {
        toRecover: i18n.t('vat_to_claim_current_year', {
          year: CURRENT_YEAR,
        }),
        toPay: i18n.t('vat_due_current_year', {
          year: CURRENT_YEAR,
        }),
      },
      pastPeriods: {
        toRecover: i18n.t('vat_to_claim_past_years'),
        toPay: i18n.t('vat_due_past_years'),
      },
    },
  };
  const labelsBasedOnUserVATFrequency =
    mapper[returnFrequency as keyof typeof mapper] ||
    mapper[VATReturnFrequencyEnum.quarterly];

  return {
    currentPeriodLabel:
      labelsBasedOnUserVATFrequency.currentPeriod[
        currentPeriodAmount > 0 ? 'toRecover' : 'toPay'
      ],
    pastPeriodsLabel:
      labelsBasedOnUserVATFrequency.pastPeriods[
        pastPeriodAmounts > 0 ? 'toRecover' : 'toPay'
      ],
  };
};

const evaluateProvisionForSpecialVATLabels = (
  returnFrequency: VATReturnFrequency,
  currentPeriodAmount: number,
  pastPeriodAmounts: number,
): {
  currentPeriodLabel: string;
  pastPeriodsLabel: string;
} => {
  const mapper = {
    [VATReturnFrequencyEnum.quarterly]: {
      currentPeriod: {
        toRecover: i18n.t('special_vat_to_claim_current_quarter', {
          quarter: CURRENT_QUARTER,
        }),
        toPay: i18n.t('special_vat_due_current_quarter', {
          quarter: CURRENT_QUARTER,
        }),
      },
      pastPeriods: {
        toRecover: i18n.t('special_vat_to_claim_past_quarters'),
        toPay: i18n.t('special_vat_due_past_quarters'),
      },
    },
    [VATReturnFrequencyEnum.monthly]: {
      currentPeriod: {
        toRecover: i18n.t('special_vat_to_claim_current_month', {
          month: dayjs(new Date(CURRENT_YEAR, CURRENT_MONTH, 1)).format('MMMM'),
        }),
        toPay: i18n.t('special_vat_due_current_month', {
          month: dayjs(new Date(CURRENT_YEAR, CURRENT_MONTH, 1)).format('MMMM'),
        }),
      },
      pastPeriods: {
        toRecover: i18n.t('special_vat_to_claim_past_months'),
        toPay: i18n.t('special_vat_due_past_months'),
      },
    },
    [VATReturnFrequencyEnum.yearly]: {
      currentPeriod: {
        toRecover: i18n.t('special_vat_to_claim_current_year', {
          year: CURRENT_YEAR,
        }),
        toPay: i18n.t('special_vat_due_current_year', {
          year: CURRENT_YEAR,
        }),
      },
      pastPeriods: {
        toRecover: i18n.t('special_vat_to_claim_past_years'),
        toPay: i18n.t('special_vat_due_past_years'),
      },
    },
  };
  const labelsBasedOnUserVATFrequency =
    mapper[returnFrequency as keyof typeof mapper] ||
    mapper[VATReturnFrequencyEnum.quarterly];

  return {
    currentPeriodLabel:
      labelsBasedOnUserVATFrequency.currentPeriod[
        currentPeriodAmount > 0 ? 'toRecover' : 'toPay'
      ],
    pastPeriodsLabel:
      labelsBasedOnUserVATFrequency.pastPeriods[
        pastPeriodAmounts > 0 ? 'toRecover' : 'toPay'
      ],
  };
};

const calculateTotalActiveBalance = (
  balances: CashAvailableBalanceType[],
): number =>
  balances
    .filter((balance: CashAvailableBalanceType) => balance.active)
    .reduce((agg: number, balance: CashAvailableBalanceType) => {
      agg += balance.baseCurrencyBalance.amount;

      return agg;
    }, 0);

const calculateActiveFactors = (factors: Nullable<CashAvailableFactorType>[]) =>
  factors
    .filter((factor) => factor?.active)
    .reduce((agg, factor) => {
      agg += factor!.amount;

      return agg;
    }, 0);

const getActiveFactors = <T>(
  factors: ({ active?: boolean | undefined | null } | undefined | null)[],
) => {
  return factors.filter((factor) => {
    if (!factor) return false;
    return !!factor.active;
  }) as T[];
};

const taxesColorsClasses = [
  'bg-[#8059c8]',
  'bg-[#9670d3]',
  'bg-[#ac87de]',
  'bg-[#c29ee9]',
  'bg-[#d8b5f4]',
  'bg-[#edccff]',
  'bg-[#f5e6ff]',
];

const smallerTaxesColorsClasses = taxesColorsClasses.filter(
  (_, i) => i % 2 === 0,
);

const balancesColorsClasses = [
  'bg-[#2563eb]',
  'bg-[#3f7af0]',
  'bg-[#5990f4]',
  'bg-[#73a6f7]',
  'bg-[#8dbcf9]',
  'bg-[#a7d2fc]',
  'bg-[#c1e8ff]',
];

const invoicesColorClass = 'bg-[#5ab7f9]';
const purchasesColorClass = 'bg-[#79c5fa]';

const smallerBalancesColorsClasses = balancesColorsClasses.filter(
  (_, i) => i % 2 === 0,
);

const getTaxesFactorsColors = (numberOfFactors: number): string[] => {
  if (numberOfFactors <= 3) return smallerTaxesColorsClasses;
  return taxesColorsClasses;
};

const getBalancesFactorsColors = (numberOfFactors: number): string[] => {
  if (numberOfFactors <= 3) return smallerBalancesColorsClasses;
  return balancesColorsClasses;
};

export {
  calculateAvailableCash,
  cashAvailableSettingsReducer,
  evaluateProvisionForVATLabels,
  calculateTotalActiveBalance,
  calculateActiveFactors,
  evaluateProvisionForSpecialVATLabels,
  getActiveFactors,
  taxesColorsClasses,
  balancesColorsClasses,
  getTaxesFactorsColors,
  getBalancesFactorsColors,
  invoicesColorClass,
  purchasesColorClass,
};
